import React from 'react';
import { Pane } from '@ui/molecules/Pane';
import { Text } from '@ui';
import { translate } from '@utils/i18n';
import { SyncHealthBadge } from '@features/Settings/Integrations/SyncHealthBadge';
import useGetPushDeviceStatus from '@hooks/requests/push-device-status.get.hook';
import { PushDeviceStateNotice } from './PushDeviceStateNotice';
import { PushDeviceActions } from './PushDeviceActions';
import { PushDeviceRow } from './PushDeviceRow';
import { isHealthyState } from './types';
import './PushDeviceStatusPanelStyles.scss';
const NAMESPACE = 'features.settings.technicians.push_device_status_panel';
/**
 * Back-office push-device status panel — InspectPoint staff only, both here and
 * on the endpoint it reads.
 *
 * A React island in the ERB technician page, modelled on the QBO and Payrix
 * settings status panels and sharing their Pane / SyncHealthBadge / status-row
 * primitives. It is a sibling of SyncStatusPanel rather than a wrapper of it:
 * that component is bound to `useGetIntegrationStatus` and an `Integration`
 * union over categories in our own database, and this data is a different shape
 * read live from an external service.
 */
export function PushDeviceStatusPanel({ statusUrl }) {
    const { data, loading, error, refresh } = useGetPushDeviceStatus(statusUrl);
    const title = translate('title', { namespace: NAMESPACE });
    const subtitle = translate('subtitle', { namespace: NAMESPACE });
    if (!data) {
        return (React.createElement(Pane, { title: title, subtitle: subtitle },
            React.createElement("div", { className: "push-device-status-panel__placeholder" }, loading ? (React.createElement(React.Fragment, null,
                React.createElement("i", { className: "fa-light fa-circle-notch fa-spin", "aria-hidden": "true" }),
                React.createElement(Text, { variant: "body" }, translate('loading', { namespace: NAMESPACE })))) : (React.createElement(Text, { variant: "body" }, translate(error ? 'load_failed' : 'loading', { namespace: NAMESPACE }))))));
    }
    const healthy = isHealthyState(data.state);
    return (React.createElement(Pane, { title: title, subtitle: subtitle, headerRight: React.createElement(SyncHealthBadge, { state: healthy ? 'healthy' : 'attention', label: translate(`badge_${data.state}`, { namespace: NAMESPACE }) }), footer: React.createElement(PushDeviceActions, { status: data, onRefresh: refresh }) },
        React.createElement(PushDeviceStateNotice, { state: data.state, platformError: data.platformError }),
        data.device && React.createElement(DeviceRows, { device: data.device }),
        React.createElement(EnvironmentRows, { status: data })));
}
function DeviceRows({ device }) {
    return (React.createElement(React.Fragment, null,
        React.createElement(PushDeviceRow, { label: translate('row_device', { namespace: NAMESPACE }), value: device.deviceModel }),
        React.createElement(PushDeviceRow, { label: translate('row_app_version', { namespace: NAMESPACE }), value: device.appVersion }),
        React.createElement(PushDeviceRow, { label: translate('row_registered_at', { namespace: NAMESPACE }), value: formatTimestamp(device.registeredAt) }),
        React.createElement(PushDeviceRow, { label: translate('row_notifications', { namespace: NAMESPACE }), value: translate(device.notificationsOn ? 'notifications_on' : 'notifications_off', { namespace: NAMESPACE }) })));
}
/**
 * The addressing block. A wrong `NOTIFICATION_PLATFORM_REALM_PREFIX` produces a
 * well-formed realm with nobody in it, so reads and writes both "succeed"
 * against a namespace no one is watching — nothing errors and nothing logs.
 * Printing the host and realm is what turns that silent failure into a glance.
 * Display only, never a control.
 */
function EnvironmentRows({ status }) {
    const notConfigured = translate('not_configured', { namespace: NAMESPACE });
    return (React.createElement(React.Fragment, null,
        React.createElement(PushDeviceRow, { label: translate('row_platform_host', { namespace: NAMESPACE }), value: status.environment.platformHost || notConfigured, monospace: true }),
        React.createElement(PushDeviceRow, { label: translate('row_realm', { namespace: NAMESPACE }), value: status.environment.realm || notConfigured, monospace: true }),
        React.createElement(PushDeviceRow, { label: translate('row_recipient', { namespace: NAMESPACE }), value: status.environment.recipientId || notConfigured, monospace: true }),
        React.createElement(PushDeviceRow, { label: translate('row_platform_status', { namespace: NAMESPACE }), value: translate(`health_${status.healthState}`, { namespace: NAMESPACE }) })));
}
/** Platform timestamps are ISO8601 strings; an unparseable one is shown raw. */
function formatTimestamp(value) {
    if (!value)
        return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return value;
    return parsed.toLocaleString();
}
export default PushDeviceStatusPanel;
